import type { UseQueryReturnType } from '@tanstack/vue-query'
import type { User } from 'firebase/auth'
import { computed, ref, watch } from 'vue'
import type { ColumnFiltersState } from '@tanstack/vue-table'
import { useProfileFeed } from '~~/src/composables/query/useProfileFeed'
import type { ElementalViewWithCounter } from '~~/src/stores/useElementalListsStore'
import type { ColumnList } from '~~/src/types/library'
import type { List } from '~~/src/types/list'
import type { ElementalTypeEnum } from '~~/src/types/elemental'
import type { GetUserPromptMode } from '~~/src/api/users'

interface ViewActivesItem {
  elementalTypeId: number
  label: string
  query?: UseQueryReturnType<List[] | null, Error>
}

type ViewActive = Partial<
  Record<ElementalTypeEnum, ElementalViewWithCounter>
> | null

export type ViewActiveMapped = Array<ElementalViewWithCounter & {
  elementalTypeId: ElementalTypeEnum
}>

export function useTableLibrary(
  viewsActives: Ref<ViewActive>,
  firebaseUser: Ref<User | null>,
  mode?: Ref<GetUserPromptMode>,
  filters?: Ref<ColumnFiltersState>,
) {
  const rawData = ref<ColumnList[]>([])
  const viewActivesData = ref<ViewActivesItem[]>([])

  const viewActivesMapped = computed<ViewActiveMapped>(() => {
    return Object.values(viewsActives.value || {}).map(view => ({
      ...view,
      elementalTypeId: view?.elementaltype,
    })).sort((a, b) => {
      if (a.elementalTypeId === 0)
        return 1
      if (b.elementalTypeId === 0)
        return -1
      return 0
    })
  })

  const allFeedData = computed(() => {
    const allPrompts: ColumnList[] = []

    viewActivesData.value.forEach((view) => {
      if (!view.query?.data)
        return

      view.query.data.forEach((item: any) => {
        if (item.id === 'all' && item.prompts?.length) {
          allPrompts.push(...item.prompts.map((prompt: any) => ({
            ...prompt,
            elementalTypeId: view.elementalTypeId,
            label: view.label,
          })))
        }
        else {
          allPrompts.push({
            ...item,
            elementalTypeId: view.elementalTypeId,
            label: view.label,
          })
        }
      })
    })

    const allPromptsFiltered = allPrompts.filter(item => item?.id !== 'all')

    const filteredPrompts = allPromptsFiltered.filter((item) => {
      return filters?.value?.every((filter) => {
        const itemValue = resolveValueByPath(item, filter.id)
        const isMatch = itemValue?.toString() === filter.value?.toString()
        return isMatch
      })
    })

    return filteredPrompts
  })

  watch([viewsActives, firebaseUser, mode], () => {
    const userId = firebaseUser.value?.uid

    if (userId && viewsActives.value) {
      viewActivesData.value = Object.values(viewsActives.value).map((view: ElementalViewWithCounter) => {
        const query = useProfileFeed(userId, {
          mode: mode?.value || 'library',
          order_by: 'name',
          elemental_type_id: view.elementaltype,
          is_knowledge_base: false,
          size: 0,
        }, {
          enabled: computed(() => !!userId),
        })

        return {
          elementalTypeId: view.elementaltype,
          label: view.label,
          query,
        }
      })
    }
  }, { immediate: true })

  const isLoading = computed(() => {
    return viewActivesData.value.length === 0 || viewActivesData.value.some(feed => feed.query?.isLoading)
  })

  return {
    rawData,
    viewActivesMapped,
    viewActivesData,
    allFeedData,
    isLoading,
  }
}
